import React, { FC, useEffect, useMemo, useState } from 'react'
import './circular-progress-bar.css'

// UTILITIES
import { flatten } from '@utils/string-helper'


interface Props extends React.SVGAttributes<SVGSVGElement> {
    ghostValue?: number
    value: number
    max: number
    label?: string
    settings?: CircularProgressBarSettings
}
interface CircularProgressBarSettings {
    /** Overrides the default line thickness that scales off of the svg size. */
    thickness?: string | number
    paletteColor?: string
    trackColor?: string
    fillColor?: string
    /** `round` by default */
    fillCap?: React.SVGAttributes<SVGCircleElement>['strokeLinecap']
    /** `right` by default. Currently only supports filling from top. */
    fillDirection?: 'left' | 'right'
}

const CircularProgressBar: FC<Props> = (props) => {
    const { className: classProps, ghostValue, value = 0, max = 1, label = '', settings, style: styleProps, ...svgProps } = props
    const { thickness, paletteColor, trackColor, fillColor, fillCap, fillDirection } = settings || {}
    const [percentage, setPercentage] = useState(1 - (Math.round((value / max) * 100) / 100))
    const [ghostPercentage, setGhostPercentage] = useState(ghostValue ? 1 - (Math.round(((value + ghostValue) / max) * 100) / 100) : 1)

    const className = flatten({ palette: !!paletteColor }, "circular-progress-bar", classProps)
    const strokeWidth = (thickness != null ? { "--_stroke-width": thickness } : {}) as React.CSSProperties

    // Splits the label at spaces and orders each word on top of one another.
    const text = useMemo(() => label.split(' ').map((string, index, arr) => (
        <text key={`${string}-${index}`} x="50%" y={`${Math.round(((index + 1) / (arr.length + 1)) * 100)}%`} dominantBaseline="middle" textAnchor="middle">{string}</text>
    )), [label])

    useEffect(() => {
        var d = 1 - (Math.round((value / max) * 100) / 100),
            percent = isNaN(d) ? 1 : d,
            ghost = ghostValue ? 1 - (Math.round(((value + ghostValue) / max) * 100) / 100) : 1
        setPercentage(percent)
        setGhostPercentage((percent !== percentage && percent === ghostPercentage) ? ghostPercentage : ghost)
    }, [value, max, ghostValue])

    return (
        <svg className={className} viewBox={`0 0 70 70`} height={`70px`} style={{ color: paletteColor, ...strokeWidth, ...styleProps }} {...svgProps}>
            <circle className="track" style={{ stroke: trackColor }} />
            {(ghostValue != null) && (
                <circle className={flatten("ghost", fillDirection)} style={{
                    strokeDashoffset: `calc(${ghostPercentage} * var(--_circumference))`,
                    strokeLinecap: fillCap,
                    stroke: fillColor,
                }} />
            )}
            <circle className={flatten("fill", fillDirection)} style={{
                strokeDashoffset: `calc(${percentage} * var(--_circumference))`,
                strokeLinecap: fillCap,
                stroke: fillColor,
            }} />
            {text}
        </svg>
    )
}

export { CircularProgressBar }